import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { syncStatusBarToTheme, setThemeColorMeta } from '@/utils/capacitor';
import {
  readThemeMode,
  persistThemeMode,
  effectiveIsDark,
  type ThemeMode,
} from '@/utils/themePreference';
import { persistThemeToDb } from '@/services/preferenceSync';
import { supabase } from '@/integrations/supabase/client';

interface ThemeContextType {
  isDarkMode: boolean;
  /** Stored preference: system follows OS; dark/light are explicit overrides */
  themeMode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (isDark: boolean) => void;
  /** Apply mode locally (localStorage + state). Used by login sync; does not write DB. */
  applyThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => readThemeMode());
  const [systemIsDark, setSystemIsDark] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  const isDarkMode = useMemo(
    () => effectiveIsDark(themeMode, systemIsDark),
    [themeMode, systemIsDark]
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemIsDark(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const applyThemeMode = useCallback((mode: ThemeMode) => {
    persistThemeMode(mode);
    setThemeModeState(mode);
  }, []);

  const setTheme = useCallback(
    (isDark: boolean) => {
      const mode: ThemeMode = isDark ? 'dark' : 'light';
      applyThemeMode(mode);

      void (async () => {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          const userId = session?.user?.id;
          if (userId) {
            await persistThemeToDb(userId, mode);
          }
        } catch (err) {
          console.warn('[ThemeContext] persist theme to DB failed:', err);
        }
      })();
    },
    [applyThemeMode]
  );

  const toggleTheme = useCallback(() => {
    const next = !effectiveIsDark(themeMode, systemIsDark);
    setTheme(next);
  }, [themeMode, systemIsDark, setTheme]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    setThemeColorMeta(isDarkMode);
    void syncStatusBarToTheme(isDarkMode);

    if (import.meta.env.DEV) {
      console.log('[ThemeProvider]', {
        platform: Capacitor.getPlatform(),
        themeMode,
        systemIsDark,
        effectiveIsDark: isDarkMode,
        rootClasses: Array.from(document.documentElement.classList),
      });
    }
  }, [isDarkMode, themeMode, systemIsDark]);

  return (
    <ThemeContext.Provider
      value={{ isDarkMode, themeMode, toggleTheme, setTheme, applyThemeMode }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
